/**
 * Ask someone for an estimate or a score (PRD-tasks-asks-and-my-work §4.1, D5).
 *
 * The ask is a one-person async session on the items, asking for one attribute
 * (effort, value, risk, or any criterion), with the person's task on it. Their
 * answer is saved the way any value typed in a session is (sessionValueEntry.ts):
 * a value proposal on each item, awaiting someone who may accept values. When
 * they have answered every item, complete_ask_answer (migration 1054) runs the
 * session, and its task completes with it (820), which withdraws the alert (1053).
 *
 * The session carries mode_config.ask so the engine shows the person who asked
 * that it is waiting (get_responsibilities row 2f).
 */
import { createSessionWithFeedback } from './sessions/createSessionFlow';
import { usePrioritizationTaskStore, whenTaskSaved } from '../stores/prioritization/prioritizationTaskStore';
import { sendNotification } from './notificationService';
import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { Attribute, MeasurementScale } from '../types/prioritization/lifecycle';
import type { ValueEntryConfig } from '../types/prioritization/prioritizationTask';

/** Days an ask stays open when the person asking names no date. */
export const ASK_DEFAULT_DAYS = 14;

/** The scale an ask uses: the attribute's default, else its first, else none. */
export function askScale(attribute: Pick<Attribute, 'scales' | 'defaultScaleId'>): MeasurementScale | null {
  const scales = attribute.scales ?? [];
  return scales.find((s) => s.id === attribute.defaultScaleId) ?? scales.find((s) => s.isDefault) ?? scales[0] ?? null;
}

/**
 * Whether a person can be asked for this attribute as an estimate or a score:
 * an evaluative attribute (effort, impact, risk, a criterion), or, where none is
 * classified, a number or a scale. Never an owner, a date, a theme or a tag.
 */
export function isEstimable(attribute: Pick<Attribute, 'semanticType' | 'type' | 'scales'>): boolean {
  if (attribute.semanticType) return attribute.semanticType === 'evaluative';
  return attribute.type === 'number' || (attribute.scales?.length ?? 0) > 0;
}

/** How the person asked enters the value, from the attribute's scale. */
export function valueEntryConfigFor(attribute: Pick<Attribute, 'scales' | 'defaultScaleId'>): ValueEntryConfig {
  const scale = askScale(attribute);
  if (!scale) return { format: 'number', allowNotes: true };
  if (scale.scaleType === 'fibonacci') return { format: 'fibonacci', allowNotes: true };
  if ((scale.scaleType === 'numeric' || scale.scaleType === 'percentage' || scale.scaleType === 'currency')
      && scale.points.length === 0) {
    return { format: 'number', min: scale.min, max: scale.max, allowNotes: true };
  }
  if (scale.points.length > 0) {
    return {
      format: 'custom',
      options: scale.points.map((p) => ({ value: p.value, label: p.label })),
      allowNotes: true,
    };
  }
  return { format: 'number', min: scale.min, max: scale.max, allowNotes: true };
}

export interface EstimateAsk {
  workspaceId: string;
  asker: { id: string; name: string };
  person: { id: string; name: string };
  attribute: Pick<Attribute, 'key' | 'label' | 'scales' | 'defaultScaleId'>;
  itemIds: string[];
  dueAt?: Date | null;
  note?: string | null;
  /** Where it was asked from, for the session's cycle and track. */
  cycleId?: string | null;
  trackId?: string | null;
}

export type EstimateAskResult = { ok: true; sessionId: string; taskId: string } | { ok: false; error: string };

export async function askForEstimate(ask: EstimateAsk): Promise<EstimateAskResult> {
  if (ask.itemIds.length === 0) return { ok: false, error: 'Choose at least one item to ask about.' };
  const n = ask.itemIds.length;
  const what = `${ask.attribute.label} on ${n === 1 ? '1 item' : `${n} items`}`;
  const opensAt = new Date();
  const closesAt = ask.dueAt ?? new Date(opensAt.getTime() + ASK_DEFAULT_DAYS * 24 * 60 * 60 * 1000);
  const scale = askScale(ask.attribute);

  const made = await createSessionWithFeedback({
    name: `${what}, from ${ask.person.name}`,
    description: ask.note ?? undefined,
    facilitatorId: ask.asker.id,
    createdBy: ask.asker.id,
    intent: 'converge_values',
    cycleId: ask.cycleId ?? undefined,
    trackId: ask.trackId ?? undefined,
    sessionContextMode: ask.trackId ? 'cycle_linked' : 'app_linked',
    initialCriteriaIds: [ask.attribute.key],
    itemIds: ask.itemIds,
    initialItemIds: ask.itemIds,
    workspaceId: ask.workspaceId,
    modeConfig: { runMode: 'open', timingMode: 'async', entryMode: 'participants', opensAt, closesAt, ask: true },
  });
  if (!made.ok || !made.sessionId) return { ok: false, error: made.error ?? 'The ask could not be started.' };

  // The person asked is not put on the session's roster: their task is their
  // part (create_session_task), and a roster entry would send a second notice
  // ("You were invited to …", 758) for the same ask.

  let taskId: string;
  try {
    const task = usePrioritizationTaskStore.getState().createTask({
      sessionId: made.sessionId,
      cycleId: ask.cycleId ?? undefined,
      name: what,
      intent: `Give your ${ask.attribute.label.toLowerCase()} for ${n === 1 ? 'this item' : 'these items'}`,
      description: ask.note ?? undefined,
      category: 'value_entry',
      attributeName: ask.attribute.key,
      valueEntryConfig: valueEntryConfigFor(ask.attribute),
      toolConfig: { ask: true, attribute_key: ask.attribute.key, scale_id: scale?.id ?? null },
      itemIds: ask.itemIds,
      mode: 'async',
      participantIds: [ask.person.id],
      facilitatorId: ask.asker.id,
      createdBy: ask.asker.id,
      opensAt,
      closesAt,
      autoOpen: true,
    });
    taskId = task.id;
    await whenTaskSaved(task.id);
  } catch (e) {
    // Nothing was asked of anyone: retire the empty session again.
    await getTypedSupabaseClient().rpc('retire_session', {
      p_session_id: made.sessionId,
      p_reason: 'The ask it was made for could not be saved.',
    });
    return { ok: false, error: e instanceof Error ? e.message : 'The ask could not be saved.' };
  }

  void sendNotification({
    event: 'task_assigned',
    channels: ['in_app'],
    recipientIds: [ask.person.id],
    senderId: ask.asker.id,
    title: `${ask.asker.name} asked you for an estimate`,
    body: `${what}${ask.dueAt ? `, by ${ask.dueAt.toLocaleDateString()}` : ''}.`,
    priority: 'normal',
    data: { taskId, sessionId: made.sessionId, attributeKey: ask.attribute.key },
    actionUrl: `/prioritization/task/${taskId}`,
    actionLabel: 'Answer',
  });

  return { ok: true, sessionId: made.sessionId, taskId };
}

/** The person asked has entered every value: finish the ask (complete_ask_answer, 1054). */
export async function finishEstimateAsk(taskId: string): Promise<{ ok: true } | { ok: false; error: string }> {
  const { error } = await getTypedSupabaseClient().rpc('complete_ask_answer', { p_task_id: taskId });
  return error ? { ok: false, error: error.message } : { ok: true };
}
